import React, { useState, useEffect, useMemo } from 'react';
import { getAllStudents } from '../utils/studentCache';
import { supabase } from '../lib/supabase';
import ImportButton from '../components/ui/ImportButton';
import { useAuth } from '../hooks/useAuth';
import toast from 'react-hot-toast';
import { Plus, X, Search, MessageSquare, Eye, Edit2, Trash2 } from 'lucide-react';
import SearchableSelect from '../components/ui/SearchableSelect';
import { sendNotification } from '../lib/notifications';
import { saveStyledXlsx } from '../lib/excelStyle';
import DateInput from '../components/ui/DateInput';
import { fmtDMY } from '../lib/dateFormat';

// PostgREST returns max 1000 rows per request — page through everything
async function fetchAllPaged(buildQuery) {
  let all = [], from = 0;
  while (true) {
    const { data, error } = await buildQuery().range(from, from + 999);
    if (error) throw error;
    all = all.concat(data || []);
    if (!data || data.length < 1000) break;
    from += 1000;
  }
  return all;
}
// Local (IST) date — toISOString() gives the UTC date, i.e. "yesterday" before 5:30am IST
const localDate = (d = new Date()) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
// whole days between a YYYY-MM-DD date and today (local)
const daysSince = ds => {
  if (!ds) return null;
  const [y, m, d] = String(ds).slice(0, 10).split('-').map(Number);
  const a = new Date(y, m - 1, d), t = new Date(); t.setHours(0, 0, 0, 0);
  return Math.round((t - a) / 86400000);
};
// status values in the data are mixed (pending / Pending, Approved / Processed ...)
const isPendingSt  = st => String(st || '').toLowerCase() === 'pending';
const isResolvedSt = st => ['approved', 'processed'].includes(String(st || '').toLowerCase());
const isRejectedSt = st => String(st || '').toLowerCase() === 'rejected';

const REQUEST_TYPES = [
  'Leave Request', 'Hostel Request', 'Fee Concession Request', 'Bonafide Request',
  'Package Change Request', 'Course Discontinue Request', 'Hostel Change', 'Batch Change',
  'Centre Change', 'Late Fee Payment', 'Certificate Authorization', 'Exam Request',
  'Experience Certificate Extension', 'Hostel Extension', 'VT Certificate',
  'Hostel Vacate', 'Other Docs Request',
];

const NEW_REQUEST_TYPES = [
  'Reference Letter Request', 'Salary Statements Request', 'NDT Certificates Request',
  'Course Mode Change Request', 'Reprint Request(NDT&NACTET)',
];

const STATUS_COLOR = { pending: 'badge-amber', Approved: 'badge-green', Rejected: 'badge-red' };

const emptyForm = {
  student_id: '', request_type: '', request_mode: '', request_date: '',
  issued_date: '', batch_coordinator_id: '', purpose: '', status: 'pending', assigned_to: '', remarks: '',
};

const newForm = () => ({ ...emptyForm, request_date: localDate() });

function sortBatches(batches) {
  // Ongoing first, then Upcoming, then Completed; inside each group the newest batch (latest start date) comes first.
  const rank = s => { const v = String(s || '').trim().toLowerCase(); return (v === 'ongoing' || v === 'active' || v === 'running') ? 0 : (v === 'completed' || v === 'closed') ? 2 : 1; };
  return [...(batches || [])].sort((a, b) => {
    const d = rank(a.status) - rank(b.status);
    if (d) return d;
    const sa = a.start_date || '', sb = b.start_date || '';
    if (sa !== sb) return sa < sb ? 1 : -1;
    return (b.batch_name || '').localeCompare(a.batch_name || '', undefined, { numeric: true });
  });
}

// Sort students by register number the natural way (TM23002 before TM23010).
const byRegNo = (a, b) => String(a.register_number || '').localeCompare(String(b.register_number || ''), undefined, { numeric: true });

export default function Requests() {
  const { canEdit, staff } = useAuth();
  const [staffUsers, setStaffUsers] = useState([]);
  const [requests, setRequests] = useState([]);
  const [students, setStudents] = useState([]);
  const [staffList, setStaffList] = useState([]);
  const [coordinators, setCoordinators] = useState([]);
  const [batches, setBatches]     = useState([]);
  const [loading, setLoading]     = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showDetail, setShowDetail] = useState(null);
  const [editing, setEditing]     = useState(null);
  const [filterTab, setFilterTab] = useState('all');
  const [search, setSearch]       = useState('');
  const [filterBatch, setFilterBatch]         = useState('');
  const [filterType, setFilterType]           = useState('');
  const [filterConcerned, setFilterConcerned] = useState('');
  const [filterMode, setFilterMode]           = useState('');
  const [filterStatus, setFilterStatus]       = useState('');
  const [showReport, setShowReport] = useState(false);
  const [selectedRows, setSelectedRows] = useState(new Set());
  const SLA_DAYS = {
    'Leave Request': 1, 'Hostel Request': 2, 'Fee Concession Request': 2,
    'Bonafide Request': 1, 'Package Change Request': 2, 'Course Discontinue Request': 2,
    'Hostel Change': 2, 'Batch Change': 2, 'Centre Change': 2, 'Late Fee Payment': 1,
    'Certificate Authorization': 2, 'Exam Request': 2,
    'Experience Certificate Extension': 2,
  };
  const [saving, setSaving]       = useState(false);
  const [form, setForm]           = useState(newForm);

  useEffect(() => { loadAll(); }, []);

  // Real-time updates (debounced) — only the requests list is reloaded
  useEffect(() => {
    let _rt = null;
    const _reload = () => { clearTimeout(_rt); _rt = setTimeout(() => { loadRequests(); }, 800); };
    const ch = supabase.channel('requests-realtime')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'student_requests' }, () => _reload())
      .subscribe();
    return () => { clearTimeout(_rt); supabase.removeChannel(ch); };
  }, []);

  async function loadRequests() {
    try {
      const rows = await fetchAllPaged(() => supabase.from('student_requests')
        .select('*, students(full_name, register_number, batch_id, batch_name, batches(batch_name))')
        .order('created_at', { ascending: false }).order('id'));
      setRequests(rows);
    } catch (e) { console.error('loadRequests error:', e); toast.error('Could not load requests: ' + e.message); }
  }

  async function loadAll() {
    const [st, b, coord, stf, hr] = await Promise.all([
      supabase.from('staff').select('id, full_name').eq('is_active', true).order('full_name').limit(200),
      supabase.from('batches').select('id, batch_name, status, start_date').order('batch_name'),
      supabase.from('hr_staff').select('id, staff_name, designation, personal_mobile').eq('is_active', true).order('staff_name'),
      supabase.from('staff').select('id, full_name, email'),
      supabase.from('hr_staff').select('staff_name, personal_email, official_email, personal_mobile').eq('is_active', true),
    ]);
    setCoordinators(coord.data || []);
    setStaffList(st.data || []);
    setBatches(sortBatches(b.data || []));
    // staffUsers = staff + phone looked up from hr_staff by email / name
    const allHrStaff = hr.data || [];
    setStaffUsers((stf.data || []).map(u => {
      const h = allHrStaff.find(x => x.official_email === u.email || x.personal_email === u.email || x.staff_name === u.full_name);
      return { ...u, personal_mobile: h?.personal_mobile || '' };
    }));
    await loadRequests();
    try { setStudents(await getAllStudents()); } catch (e) { toast.error('Could not load students: ' + e.message); }
    setLoading(false);
  }

  async function save() {
    if (!form.student_id) { toast.error('Select a student'); return; }
    if (!form.request_type) { toast.error('Select request type'); return; }
    setSaving(true);
    const payload = {
      student_id:      form.student_id,
      request_type:    form.request_type,
      batch_coordinator_id: form.batch_coordinator_id || null,
      request_mode:    form.request_mode || null,
      request_date:    form.request_date || null,
      issued_date:     form.issued_date || null,
      purpose:         form.purpose || form.request_type || 'N/A',
      status:          form.status || 'pending',
      // new requests default to the logged-in staff; an edit keeps whatever Concerned Person is chosen
      action_taken_by: form.assigned_to || (editing ? null : (staff?.id || null)),
      remarks:         form.remarks || null,
    };
    // approving / processing a request stamps the issued date if it is still empty
    if ((isResolvedSt(payload.status)) && !payload.issued_date) payload.issued_date = localDate();
    const prev = editing ? requests.find(r => r.id === editing) : null;
    const { error } = editing
      ? await supabase.from('student_requests').update(payload).eq('id', editing)
      : await supabase.from('student_requests').insert(payload);
    if (error) toast.error(error.message);
    else {
      toast.success(editing ? 'Request updated' : 'Request recorded');
      // Tell the concerned person (only them) when the request is new, re-assigned, or its status changed
      const changed = !prev || prev.status !== payload.status || prev.action_taken_by !== payload.action_taken_by;
      if (form.assigned_to && changed) {
        const studentName = modalStudents.find(s => s.id === form.student_id)?.full_name || 'A student';
        await sendNotification({
          title: `📋 Student Request — ${String(form.status || 'pending').toUpperCase()}`,
          message: `${studentName}'s request (${form.request_type}) is ${form.status}. Please take necessary action.`,
          type: isPendingSt(form.status) ? 'warning' : isResolvedSt(form.status) ? 'success' : 'info',
          link: '/requests',
          createdBy: staff?.id,
          studentId: form.student_id,
          staffId: form.assigned_to,
        });
      }
      setShowModal(false); setEditing(null); setForm(newForm()); setModalBatch('');
      setFilterTab('all');
      await loadRequests();
    }
    setSaving(false);
  }

  async function deleteReq(id) {
    if (!window.confirm('Delete this request?')) return;
    const { error } = await supabase.from('student_requests').delete().eq('id', id);
    if (error) toast.error(error.message);
    else { toast.success('Deleted'); loadRequests(); }
  }

  function openEdit(r) {
    setEditing(r.id);
    const editStu = students.find(s => s.id === r.student_id);
    setModalBatch(editStu?.batch_name || r.students?.batch_name || '');
    setForm({
      student_id:   r.student_id || '',
      request_type: r.request_type || r.category || '',
      batch_coordinator_id: r.batch_coordinator_id || '',
      request_mode: r.request_mode || '',
      request_date: r.request_date || '',
      issued_date:  r.issued_date || '',
      purpose:      r.purpose || '',
      status:       r.status || 'pending',
      assigned_to:  r.action_taken_by || '',
      remarks:      r.remarks || '',
    });
    setShowModal(true);
  }

  const filtered = requests.filter(r => {
    const q = search.toLowerCase();
    const matchSearch    = !q             || r.students?.full_name?.toLowerCase().includes(q) || r.students?.register_number?.toLowerCase().includes(q) || r.request_type?.toLowerCase().includes(q);
    const matchBatch     = !filterBatch   || (r.students?.batches?.batch_name || r.students?.batch_name) === filterBatch;
    const matchTab       = filterTab === 'all'
      || (filterTab === 'pending'  && isPendingSt(r.status))
      || (filterTab === 'Approved' && isResolvedSt(r.status))
      || (filterTab === 'Rejected' && isRejectedSt(r.status));
    const matchType      = !filterType      || r.request_type === filterType;
    const matchConcerned = !filterConcerned || r.action_taken_by === filterConcerned;
    const matchMode      = !filterMode      || r.request_mode === filterMode;
    const matchStatus = !filterStatus || (
      filterStatus==='__overdue__'
        ? (() => {
            const sla = SLA_DAYS[r.request_type]||SLA_DAYS[r.category]||null;
            const days = isPendingSt(r.status) ? daysSince(r.request_date) : null;
            return sla && days !== null && days > sla;
          })()
        : filterStatus==='pending' ? isPendingSt(r.status)
        : filterStatus==='Approved' ? r.status && String(r.status).toLowerCase()==='approved'
        : filterStatus==='Processed' ? String(r.status||'').toLowerCase()==='processed'
        : filterStatus==='Rejected' ? isRejectedSt(r.status)
        : r.status === filterStatus
    );
    return matchSearch && matchBatch && matchTab && matchType && matchConcerned && matchMode && matchStatus;
  });

  function closeModal() { setShowModal(false); setEditing(null); setForm(newForm()); setModalBatch(''); }

  // Approve the selected requests — only the ones still pending (rejected / already-approved rows are left alone)
  async function approveSelected() {
    const ids = requests.filter(r => selectedRows.has(r.id) && isPendingSt(r.status)).map(r => r.id);
    const skipped = selectedRows.size - ids.length;
    if (!ids.length) { toast.error('None of the selected requests are pending'); return; }
    if (!window.confirm(`Approve ${ids.length} pending request${ids.length > 1 ? 's' : ''}?${skipped ? `\n(${skipped} selected request(s) are not pending and will be skipped.)` : ''}`)) return;
    let failed = 0;
    for (let i = 0; i < ids.length; i += 100) {
      const chunk = ids.slice(i, i + 100);
      const r1 = await supabase.from('student_requests').update({ status: 'Approved' }).in('id', chunk);
      if (r1.error) { failed += chunk.length; toast.error(r1.error.message); continue; }
      // issued date only where it is still empty
      await supabase.from('student_requests').update({ issued_date: localDate() }).in('id', chunk).is('issued_date', null);
    }
    if (failed < ids.length) toast.success(`${ids.length - failed} request(s) approved`);
    setSelectedRows(new Set());
    loadRequests();
  }

  function exportList() {
    if (!filtered.length) { toast.error('Nothing to export'); return; }
    const rows = filtered.map((r, i) => {
      const sla = SLA_DAYS[r.request_type] || SLA_DAYS[r.category] || null;
      const dp = isPendingSt(r.status) ? daysSince(r.request_date) : null;
      return {
        'S.No': i + 1, Student: r.students?.full_name || '', 'Reg No': r.students?.register_number || '',
        Batch: r.students?.batches?.batch_name || r.students?.batch_name || '',
        'Request Type': r.request_type || r.category || '', Mode: r.request_mode || '',
        'Request Date': r.request_date || '', 'Issued Date': r.issued_date || '',
        'Days Pending': dp ?? '', 'SLA (days)': sla ?? '', Overdue: sla && dp !== null && dp > sla ? 'Yes' : '',
        'Assigned To': staffList.find(x => x.id === r.action_taken_by)?.full_name || '',
        Status: r.status || '', Purpose: r.purpose || '', Remarks: r.remarks || '',
      };
    });
    saveStyledXlsx(`Student_Requests_${localDate()}.xlsx`, [{ name: 'Requests', title: 'Student Requests — Armstech Engineers', rows }]);
  }

  const uniqueTypes = [...new Set(requests.map(r => r.request_type).filter(Boolean))].sort();

  // Student list filtered by batch for modal
  const [modalBatch, setModalBatch] = useState('');
  const modalStudents = useMemo(() => (modalBatch ? students.filter(s => s.batch_name === modalBatch) : [...students]).sort((a,b)=>(a.register_number||'').localeCompare(b.register_number||'')), [students, modalBatch]);

  return (
    <div className="page">
      <div className="page-header">
        <div><h1 className="page-title">Student Requests</h1><p className="page-subtitle">Manage student requests and track status</p></div>
        <div style={{display:"flex",gap:8,flexWrap:'wrap'}}><ImportButton configKey="student_requests" onImported={()=>loadRequests()}/>{canEdit('requests') && <button className="btn btn-primary" onClick={()=>{setEditing(null);setForm(newForm());setModalBatch('');setShowModal(true);}}><Plus size={14}/> New Request</button>}
        <button className="btn btn-ghost btn-sm" onClick={exportList}>⬇ Export</button>
        <button className={`btn btn-sm ${showReport?'btn-primary':'btn-ghost'}`} onClick={()=>setShowReport(r=>!r)}>📊 Report</button>
        {selectedRows.size>0&&canEdit('requests')&&(
          <button className="btn btn-sm" style={{background:'var(--green-light)',color:'var(--green)'}} onClick={approveSelected}>
            ✅ Approve {selectedRows.size} Selected
          </button>
        )}
      </div>
      </div>

      {/* Report */}
      {showReport && (
        <div className="card" style={{marginBottom:16}}>
          <div style={{fontWeight:700,fontSize:14,marginBottom:12}}>📊 Request Report</div>
          {(() => {
            const isPending = isPendingSt;
            const isResolved = isResolvedSt;
            const typeStats = {};
            requests.forEach(r => {
              const t = r.request_type || r.category || 'Other';
              if (!typeStats[t]) typeStats[t] = {total:0,pending:0,approved:0,overdue:0,totalDays:0,resolvedCount:0};
              typeStats[t].total++;
              if (isPending(r.status)) {
                typeStats[t].pending++;
                const sla = SLA_DAYS[t]||null;
                const days = daysSince(r.request_date) ?? 0;
                if (sla && days > sla) typeStats[t].overdue++;
              }
              if (isResolved(r.status)) {
                typeStats[t].approved++;
                if (r.request_date && r.issued_date) {
                  const days = Math.floor((new Date(r.issued_date)-new Date(r.request_date))/86400000);
                  if (!isNaN(days) && days >= 0) { typeStats[t].totalDays += days; typeStats[t].resolvedCount++; }
                }
              }
            });
            const rows = Object.entries(typeStats).sort((a,b)=>b[1].total-a[1].total);
            const totalOverdue = rows.reduce((s,[,v])=>s+v.overdue,0);
            return (
              <>
                <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(140px,1fr))',gap:10,marginBottom:12}}>
                  {[
                    {label:'Total Requests',value:requests.length,color:'var(--accent)',filter:''},
                    {label:'Pending',value:requests.filter(r=>isPending(r.status)).length,color:'var(--amber)',filter:'pending'},
                    {label:'Overdue',value:totalOverdue,color:'var(--red)',filter:'__overdue__'},
                    {label:'Resolved',value:requests.filter(r=>isResolved(r.status)).length,color:'var(--green)',filter:'Approved'},
                  ].map(s=>(
                    <div key={s.label} className="card" style={{textAlign:'center',padding:10,cursor:'pointer',
                      border:filterStatus===s.filter&&s.filter!==''?'2px solid var(--accent)':'2px solid transparent'}}
                      onClick={()=>setFilterStatus(filterStatus===s.filter?'':s.filter||'')}>
                      <div style={{fontSize:20,fontWeight:800,color:s.color}}>{s.value}</div>
                      <div style={{fontSize:11,color:'var(--text3)'}}>{s.label}</div>
                      <div style={{fontSize:9,color:'var(--accent)',marginTop:2}}>{filterStatus===s.filter&&s.filter!==''?'✕ Clear':'Click to filter'}</div>
                    </div>
                  ))}
                </div>
                <div style={{overflowX:'auto'}}>
                  <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
                    <thead>
                      <tr style={{background:'var(--accent-light)'}}>
                        {['Request Type','Total','Pending','🚨 Overdue','Approved','Avg Resolution'].map(h=>(
                          <th key={h} style={{padding:'6px 10px',textAlign:'left',whiteSpace:'nowrap'}}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map(([type,s])=>(
                        <tr key={type} style={{borderBottom:'1px solid var(--border)'}}>
                          <td style={{padding:'7px 10px',fontWeight:500}}>{type}</td>
                          <td style={{padding:'7px 10px',textAlign:'center'}}>{s.total}</td>
                          <td style={{padding:'7px 10px',textAlign:'center',color:s.pending>0?'var(--amber)':'var(--text3)'}}>{s.pending||'—'}</td>
                          <td style={{padding:'7px 10px',textAlign:'center',color:s.overdue>0?'var(--red)':'var(--text3)',fontWeight:s.overdue>0?700:400}}>{s.overdue||'—'}</td>
                          <td style={{padding:'7px 10px',textAlign:'center',color:'var(--green)'}}>{s.approved||'—'}</td>
                          <td style={{padding:'7px 10px',textAlign:'center',color:'var(--text3)'}}>
                            {s.resolvedCount>0?`${(s.totalDays/s.resolvedCount).toFixed(1)} days`:'—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            );
          })()}
        </div>
      )}

      {/* Stats */}
      <div className="stats-grid" style={{ marginBottom: 20 }}>
        {[
          { label: 'Total', value: requests.length, color: 'var(--accent)', tab: 'all' },
          { label: 'Pending', value: requests.filter(r => isPendingSt(r.status)).length, color: 'var(--amber)', tab: 'pending' },
          { label: 'Approved / Processed', value: requests.filter(r => isResolvedSt(r.status)).length, color: 'var(--green)', tab: 'Approved' },
          { label: 'Rejected', value: requests.filter(r => isRejectedSt(r.status)).length, color: 'var(--red)', tab: 'Rejected' },
        ].map(s => (
          <div key={s.label} className="stat-card" style={{ cursor: 'pointer' }} onClick={() => { setFilterTab(s.tab); setFilterStatus(''); }}>
            <div className="stat-label">{s.label}</div>
            <div className="stat-value" style={{ color: s.color, fontSize: 24 }}>{s.value}</div>
          </div>
        ))}
      </div>

      {/* Filter tabs */}
      <div style={{ display: 'flex', gap: 2, marginBottom: 12 }}>
        {[['all','All'],['pending','Pending'],['Approved','Approved'],['Rejected','Rejected']].map(([v,l]) => (
          <button key={v} onClick={() => { setFilterTab(v); setFilterStatus(''); }} style={{ padding: '8px 16px', border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 13, fontWeight: 500, color: filterTab === v ? 'var(--accent)' : 'var(--text2)', borderBottom: filterTab === v ? '2px solid var(--accent)' : '2px solid transparent', fontFamily: 'var(--font-body)' }}>{l}</button>
        ))}
      </div>

      {/* Search + filters */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
        <div className="search-bar">
          <Search /><input placeholder="Search student, reg no..." value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <SearchableSelect options={batches.map(b=>({value:b.batch_name,label:b.batch_name}))} value={filterBatch} onChange={v => setFilterBatch(v)} placeholder="All Batches" searchPlaceholder="Search batch..." style={{ minWidth: 130, maxWidth: 150 }} />
        <select style={{ width: 130 }} value={filterType} onChange={e => setFilterType(e.target.value)}>
          <option value="">Request Type</option>
          {uniqueTypes.map(t => <option key={t} value={t}>{t}</option>)}
        </select>
        <select style={{ width: 130 }} value={filterMode} onChange={e => setFilterMode(e.target.value)}>
          <option value="">Request Mode</option>
          {[...new Set(['Email','Letter',...requests.map(r=>r.request_mode).filter(Boolean)])].map(m=><option key={m} value={m}>{m}</option>)}
        </select>
        <select style={{ width: 140 }} value={filterConcerned} onChange={e => setFilterConcerned(e.target.value)}>
          <option value="">Concerned Person</option>
          {staffList.map(s => <option key={s.id} value={s.id}>{s.full_name}</option>)}
        </select>
        <select style={{ width: 120 }} value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
          <option value="">All Status</option>
          <option value="pending">Pending</option>
          <option value="Approved">Approved</option>
          <option value="Processed">Processed</option>
          <option value="Rejected">Rejected</option>
        </select>
        {(search || filterBatch || filterType || filterMode || filterConcerned || filterStatus) && (
          <button className="btn btn-ghost btn-sm" onClick={() => { setSearch(''); setFilterBatch(''); setFilterType(''); setFilterMode(''); setFilterConcerned(''); setFilterStatus(''); }}>✕ Clear</button>
        )}
      </div>

      {loading ? <div className="loading"><div className="spinner" /></div> : (
        <div className="table-wrap">
          <table>
            <thead>
              <tr><th><input type='checkbox' checked={filtered.length>0&&filtered.every(r=>selectedRows.has(r.id))} onChange={e=>{if(e.target.checked)setSelectedRows(new Set(filtered.map(r=>r.id)));else setSelectedRows(new Set());}}/></th><th>Student</th><th>Batch</th><th>Request Type</th><th>Mode</th><th>Request Date</th><th>Issued Date</th><th>SLA</th><th>Assigned To</th><th>Status</th><th>Actions</th></tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr><td colSpan={11}><div className="empty-state"><MessageSquare /><h3>No requests found</h3></div></td></tr>
              ) : filtered.map(r => {
                const sla = SLA_DAYS[r.request_type] || SLA_DAYS[r.category] || null;
                const daysPending = isPendingSt(r.status) ? daysSince(r.request_date) : null;
                const isOverdue = sla && daysPending !== null && daysPending > sla;
                const isDueSoon = sla && daysPending !== null && daysPending >= sla-1 && !isOverdue;
                return (
                <tr key={r.id} style={{background:isOverdue?'rgba(239,68,68,0.05)':isDueSoon?'rgba(245,158,11,0.05)':undefined}}>
                  <td><input type="checkbox" checked={selectedRows.has(r.id)} onChange={e=>{
                    const next = new Set(selectedRows);
                    e.target.checked ? next.add(r.id) : next.delete(r.id);
                    setSelectedRows(next);
                  }}/></td>
                  <td>
                    <div style={{ fontWeight: 500 }}>{r.students?.full_name}</div>
                    <div style={{ fontSize: 11, fontFamily: 'monospace', color: 'var(--text3)' }}>{r.students?.register_number}</div>
                  </td>
                  <td style={{ fontSize: 12 }}>{r.students?.batches?.batch_name || r.students?.batch_name || '—'}</td>
                  <td><span className="badge badge-blue" style={{ fontSize: 11 }}>{r.request_type || r.category || '—'}</span></td>
                  <td style={{ fontSize: 12 }}>{r.request_mode || '—'}</td>
                  <td style={{ fontSize: 12 }}>{fmtDMY(r.request_date) || '—'}</td>
                  <td style={{ fontSize: 12 }}>{fmtDMY(r.issued_date) || '—'}</td>
                  <td style={{ fontSize: 12 }}>
                    {sla ? (
                      <div style={{fontSize:11}}>
                        <div style={{color:isOverdue?'var(--red)':isDueSoon?'var(--amber)':'var(--text3)',fontWeight:isOverdue||isDueSoon?700:400}}>
                          {daysPending!==null?`Day ${daysPending}/${sla}`:`${sla}d target`}
                        </div>
                        {isOverdue&&<div style={{color:'var(--red)',fontSize:10}}>🚨 Overdue</div>}
                        {isDueSoon&&<div style={{color:'var(--amber)',fontSize:10}}>⚠️ Due soon</div>}
                      </div>
                    ) : '—'}
                  </td>
                  <td style={{ fontSize: 12 }}>{staffList.find(s => s.id === r.action_taken_by)?.full_name || '—'}</td>
                  <td><span className={`badge ${STATUS_COLOR[r.status] || (isPendingSt(r.status) ? 'badge-amber' : isResolvedSt(r.status) ? 'badge-green' : 'badge-gray')}`}>{r.status}</span></td>
                  <td>
                    <div style={{ display: 'flex', gap: 4 }}>
                      <button className="btn btn-ghost btn-sm" onClick={() => setShowDetail(r)} title="View"><Eye size={13} /></button>
                      {canEdit('requests') && <button className="btn btn-ghost btn-sm" onClick={() => openEdit(r)} title="Edit"><Edit2 size={13} /></button>}
                      <button className="btn btn-ghost btn-sm" style={{color:'#25d366'}} title="Send WhatsApp to concerned person"
                        onClick={()=>{
                          const concerned = staffUsers.find(s => s.id === r.action_taken_by);
                          const rawPhone = (concerned?.personal_mobile||'').split('/')[0].trim(); const phone = rawPhone.replace(/[^0-9]/g,'').slice(-10);
                          const msg = encodeURIComponent(`Student Request\nType: ${r.request_type||''}\nStudent: ${r.students?.full_name||''}\nBatch: ${r.students?.batch_name||''}\nStatus: ${r.status||''}\nRemarks: ${r.remarks||''}\nPlease follow up.`);
                          if (phone) window.open(`whatsapp://send?phone=91${phone}&text=${msg}`);
                          else toast.error('No phone number for concerned person');
                        }}>💬</button>
                      {canEdit('requests') && <button className="btn btn-ghost btn-sm" style={{ color: 'var(--red)' }} onClick={() => deleteReq(r.id)} title="Delete"><Trash2 size={13} /></button>}
                    </div>
                  </td>
                </tr>
              );})}
            </tbody>
          </table>
        </div>
      )}

      {/* VIEW MODAL */}
      {showDetail && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setShowDetail(null)}>
          <div className="modal">
            <div className="modal-header">
              <span className="modal-title">Request Details</span>
              <button className="btn btn-ghost btn-sm" onClick={() => setShowDetail(null)}><X size={16} /></button>
            </div>
            <div className="modal-body">
              {[
                ['Student', showDetail.students?.full_name + ' (' + showDetail.students?.register_number + ')'],
                ['Batch', showDetail.students?.batches?.batch_name || showDetail.students?.batch_name],
                ['Request Type', showDetail.request_type || showDetail.category],
                ['Request Mode', showDetail.request_mode],
                ['Request Date', showDetail.request_date],
                ['Issued Date', showDetail.issued_date],
                ['Batch Coordinator', coordinators.find(c => c.id === showDetail.batch_coordinator_id)?.staff_name],
                ['Status', showDetail.status],
                ['Assigned To', staffList.find(s => s.id === showDetail.action_taken_by)?.full_name],
                ['Purpose', showDetail.purpose],
                ['Remarks', showDetail.remarks],
              ].map(([l,v]) => v ? <div key={l} className="detail-row"><div className="detail-label">{l}</div><div className="detail-value">{v}</div></div> : null)}
            </div>
            <div className="modal-footer">
              {canEdit('requests') && <button className="btn btn-secondary" onClick={() => { setShowDetail(null); openEdit(showDetail); }}><Edit2 size={13} /> Edit</button>}
              <button className="btn btn-primary" onClick={() => setShowDetail(null)}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* ADD/EDIT MODAL */}
      {showModal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && closeModal()}>
          <div className="modal modal-lg">
            <div className="modal-header">
              <span className="modal-title">{editing ? 'Edit Request' : 'New Student Request'}</span>
              <button className="btn btn-ghost btn-sm" onClick={closeModal}><X size={16} /></button>
            </div>
            <div className="modal-body">
              <div className="form-grid">

                {/* Batch filter for student selection */}
                <div className="form-group">
                  <label>Search by Batch</label>
                  <SearchableSelect
                    options={batches.map(b => ({ value: b.batch_name, label: b.batch_name }))}
                    value={modalBatch}
                    onChange={v => { setModalBatch(v); setForm(f => ({ ...f, student_id: '' })); }}
                    placeholder="All Batches"
                    searchPlaceholder="Search batch..."
                  />
                </div>

                <div className="form-group">
                  <label>Student *</label>
                  <SearchableSelect
                    options={[...modalStudents].sort(byRegNo).map(s => ({ value: s.id, label: `${s.full_name} (${s.register_number})` }))}
                    value={form.student_id}
                    onChange={v => setForm(f => ({ ...f, student_id: v }))}
                    placeholder="Select Student"
                    searchPlaceholder="Search student..."
                  />
                </div>

                {/* Auto-filled student details */}
                {form.student_id && (() => {
                  const s = modalStudents.find(st => st.id === form.student_id);
                  if (!s) return null;
                  return (
                    <div className="form-group full">
                      <div style={{ background: 'var(--accent-light)', border: '1px solid var(--accent)', borderRadius: 8, padding: '10px 14px', fontSize: 12, display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                        {s.contact_number && <span>📞 <strong>{s.contact_number}</strong></span>}
                        {s.whatsapp_number && <span>💬 <strong>{s.whatsapp_number}</strong></span>}
                        {s.guardian_name && <span>👤 Guardian: <strong>{s.guardian_name}</strong></span>}
                        {s.course_packages?.course_name && <span>📦 <strong>{s.course_packages.course_name}</strong></span>}
                      </div>
                    </div>
                  );
                })()}

                <div className="form-group">
                  <label>Request Type *</label>
                  <SearchableSelect
                    options={[...REQUEST_TYPES, ...NEW_REQUEST_TYPES].map(t => ({ value: t, label: t }))}
                    value={form.request_type || ''}
                    onChange={v => setForm(f => ({ ...f, request_type: v }))}
                    placeholder="Select request type..."
                    searchPlaceholder="Search request type..."
                  />
                </div>

                <div className="form-group">
                  <label>Request Mode</label>
                  <select value={form.request_mode} onChange={e => setForm(f => ({ ...f, request_mode: e.target.value }))}>
                    <option value="">Select Mode</option>
                    <option value="Email">Email</option>
                    <option value="Letter">Letter</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Request Date</label>
                  <DateInput value={form.request_date} onChange={e => setForm(f => ({ ...f, request_date: e.target.value }))} />
                </div>

                <div className="form-group">
                  <label>Issued Date</label>
                  <DateInput value={form.issued_date} onChange={e => setForm(f => ({ ...f, issued_date: e.target.value }))} />
                </div>

                <div className="form-group">
                  <label>Batch Coordinator</label>
                  <SearchableSelect
                    options={coordinators.map(s => ({ value: s.id, label: s.staff_name }))}
                    value={form.batch_coordinator_id || ''}
                    onChange={v => setForm(f => ({ ...f, batch_coordinator_id: v }))}
                    placeholder="Select coordinator..."
                    searchPlaceholder="Search coordinator..."
                  />
                </div>

                <div className="form-group">
                  <label>Status</label>
                  <select value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value }))}>
                    <option value="pending">Pending</option>
                    <option value="Approved">Approved</option>
                    <option value="Processed">Processed</option>
                    <option value="Rejected">Rejected</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Concerned Person</label>
                  <SearchableSelect
                    options={staffList.map(s=>({value:s.id,label:s.full_name}))}
                    value={form.assigned_to||''}
                    onChange={v=>setForm(f=>({...f,assigned_to:v}))}
                    placeholder="Select staff..."
                    searchPlaceholder="Search staff..."
                  />
                </div>

                <div className="form-group full">
                  <label>Purpose / Details</label>
                  <textarea value={form.purpose} onChange={e => setForm(f => ({ ...f, purpose: e.target.value }))} rows={3} placeholder="Describe the request..." />
                </div>

                <div className="form-group full">
                  <label>Remarks</label>
                  <textarea value={form.remarks} onChange={e => setForm(f => ({ ...f, remarks: e.target.value }))} rows={2} placeholder="Optional remarks..." />
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={closeModal}>Cancel</button>
              <button className="btn btn-primary" onClick={save} disabled={saving}>{saving ? 'Saving...' : editing ? 'Update' : 'Save Request'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
